// SequoiaAIPage.js
import React, { useEffect, useState } from 'react';

const SequoiaAIPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Initialize any required services or data
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);
    // Sample features for the Sequoia AI page
  const features = [
    {
      title: 'Sustainable Learning Infrastructure',
      description: 'Sequoia AI runs exclusively on recycled hardware and green energy sources, demonstrating that powerful educational tools can have minimal environmental impact.',
      icon: 'fas fa-leaf'
    },
    {
      title: 'Personalized Educational Assistance',
      description: 'Adaptive learning technology that adjusts to each student\'s unique pace, style, and educational needs to maximize learning outcomes.',
      icon: 'fas fa-brain'
    },
    {
      title: 'Interactive Study Support',
      description: 'Help students understand complex topics through interactive explanations, practice exercises, and immediate, constructive feedback.',
      icon: 'fas fa-graduation-cap' 
    },
    {
      title: 'Research Companion',
      description: 'Guide students through research processes, helping them find credible sources and develop critical thinking skills.',
      icon: 'fas fa-search'
    },
    {
      title: 'Multilingual Learning',
      description: 'Break down language barriers with support for multiple languages, making education more accessible globally while running on sustainable infrastructure.',
      icon: 'fas fa-globe'
    },
    {
      title: 'Educational Progress Tracking',
      description: 'Visualize learning progress over time, helping students and educators identify strengths and areas for improvement.',
      icon: 'fas fa-chart-line'
    }
  ];
    // Sample case studies
  const caseStudies = [
    {
      company: 'Greenfield School District',
      industry: 'K-12 Education',
      results: '27% improvement in science assessment scores',
      testimonial: 'Sequoia AI has transformed our approach to teaching sustainability science. Students love that their AI learning assistant runs on the same green technology they\'re studying.'
    },
    {
      company: 'EcoLearn University',
      industry: 'Higher Education',
      results: '40% increase in student engagement',
      testimonial: 'Our environmental science students are inspired by using an AI that practices what we teach. The personalized learning support has dramatically improved participation and outcomes.'
    }
  ];
  
  return (
    <div className="sequoia-ai-page">
      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Sequoia AI...</p>
        </div>
      ) : (
        <>          {/* Hero Section */}
          <section className="sequoia-hero">
            <div className="container">
              <h1>Sequoia AI</h1>
              <p className="sequoia-hero-subtitle">
                Sustainable educational assistance for students and learners
              </p>
              <div className="sequoia-hero-content">
                <div className="sequoia-hero-text">
                  <p>
                    Sequoia AI is our innovative educational assistant designed to help students learn 
                    and grow while demonstrating sustainable computing practices. Running exclusively on 
                    recycled hardware and powered by 100% green energy, Sequoia AI shows that cutting-edge 
                    educational technology can also be environmentally responsible.
                  </p>
                  <div className="sequoia-hero-cta">
                    <a href="#demo" className="btn-primary">Try Sequoia AI</a>
                    <a href="#features" className="btn-secondary">Explore Features</a>
                  </div>
                </div>
                <div className="sequoia-hero-image">
                  <img src="/assets/images/sequoia-ai-illustration.svg" alt="Sequoia AI Educational Assistant Visualization" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Features Section */}
          <section id="features" className="sequoia-features-section">
            <div className="container">
              <h2>Key Features</h2>
              <div className="sequoia-features-grid">
                {features.map((feature, index) => (
                  <div key={index} className="sequoia-feature-box">
                    <div className="feature-icon">
                      <i className={feature.icon}></i>
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
            {/* How It Works */}
          <section className="sequoia-how-it-works">
            <div className="container">
              <h2>How Sequoia AI Works</h2>
              <div className="process-steps">
                <div className="process-step">
                  <div className="step-number">1</div>
                  <h3>Sustainable Processing</h3>
                  <p>Sequoia AI runs on a network of refurbished servers powered entirely by renewable energy sources.</p>
                </div>
                <div className="process-step">
                  <div className="step-number">2</div>
                  <h3>Student Connection</h3>
                  <p>Students connect with Sequoia AI through our platform that works on any device, minimizing the need for new hardware.</p>
                </div>
                <div className="process-step">
                  <div className="step-number">3</div>
                  <h3>Personalized Education</h3>
                  <p>The AI adapts to each student's learning style, providing customized educational support and guidance.</p>
                </div>
                <div className="process-step">
                  <div className="step-number">4</div>
                  <h3>Progress Tracking</h3>
                  <p>Both students and educators can track learning progress while feeling good about the minimal environmental impact.</p>
                </div>
              </div>
            </div>
          </section>
          
          {/* Case Studies */}
          <section className="sequoia-case-studies">
            <div className="container">
              <h2>Success Stories</h2>
              <div className="case-studies-grid">
                {caseStudies.map((study, index) => (
                  <div key={index} className="case-study-card">
                    <h3>{study.company}</h3>
                    <p className="industry"><strong>Industry:</strong> {study.industry}</p>
                    <p className="results"><strong>Results:</strong> {study.results}</p>
                    <blockquote>"{study.testimonial}"</blockquote>
                  </div>
                ))}
              </div>
            </div>
          </section>
            {/* Demo Request Section */}
          <section id="demo" className="sequoia-demo-request">
            <div className="container">
              <h2>Experience Sequoia AI</h2>
              <p>Get a free trial of our educational assistant for your school or learning institution.</p>
              <form className="demo-form">
                <div className="form-group">
                  <input type="text" placeholder="Full Name" required />
                </div>
                <div className="form-group">
                  <input type="email" placeholder="School Email" required />
                </div>
                <div className="form-group">
                  <input type="text" placeholder="School/Institution" required />
                </div>
                <div className="form-group">
                  <select required>
                    <option value="" disabled selected>Education Level</option>
                    <option value="elementary">Elementary School</option>
                    <option value="middle">Middle School</option>
                    <option value="high">High School</option>
                    <option value="college">College/University</option>
                    <option value="continuing">Continuing Education</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <button type="submit" className="btn-primary">Start Free Trial</button>
              </form>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

// Make component available globally
window.SequoiaAIPage = SequoiaAIPage;

export default SequoiaAIPage;
